// Create / edit a retainer — the ongoing engagement that carries the income.
//
// The monthly amount is a PREFILL, not a contract figure: retainer amounts change
// month to month and the invoice records what was actually billed.
//
// The issuing partner is a PREFILL too, since migration 044. It used to be locked once
// invoices existed, because back then it WAS every invoice's issuer and editing it
// retroactively moved collected money between the partners' balances. Each invoice now
// stores its own, so this field only seeds the next recording: changing it touches no
// existing invoice and no balance, and there is nothing left to lock.
//
// The term is two dates and both may be left empty. A NEW retainer prefills today as
// its start — VISIBLY, in a field that can be changed before saving. Until migration
// 043 the server did that silently, which is why every retainer created before it
// carries the date it was typed in rather than the date the engagement began. A
// prefill the partner can see and correct is the whole difference.
//
// THE SAME FORM CREATES A ONE-OFF PROJECT (migration 067): an engagement billed one fixed
// amount for its whole term. On create the screen decides which (`billing`), so each list
// creates the kind it shows. On edit the type is a field, locked once the engagement has
// any invoice — a monthly engagement's invoices carry months and a project's carry none,
// and the server refuses the switch (billing_mode_locked_has_invoices). The amount field
// follows the type: a monthly prefill, or the project's price.
import { useState } from 'react';
import useAsync from '../../hooks/useAsync';
import { useLookups } from '../../context/LookupsContext';
import { useReloadAlerts } from '../../context/AlertsContext';
import { retainersApi, clientsApi, authApi } from '../../api';
import he from '../../i18n/he';
import { issuerOptions } from '../../utils/issuer';
import Modal from '../../components/Modal';
import { TextInput, NumberInput, DateInput, Select } from '../../components/Form';
import ClientPicker from '../../components/ClientPicker';
import { ErrorText } from '../../components/Loader';

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function RetainerModal({ retainer, billing = 'monthly', onClose, onSaved }) {
  const lookups = useLookups();
  const { data } = useAsync(() => Promise.all([clientsApi.list('customers'), authApi.partners()]));
  const isNew = !retainer || !retainer.id;
  const modeLocked = !isNew && Array.isArray(retainer.invoices) && retainer.invoices.length > 0;

  const [form, setForm] = useState({
    billing_mode: retainer?.billing_mode || billing,
    fixed_amount: retainer?.fixed_amount ?? '',
    title: retainer?.title || '',
    client_id: retainer?.client_id || '',
    revenue_stream: retainer?.revenue_stream || lookups.streamOptions[0]?.value || '',
    issuing_partner_id: retainer?.issuing_partner_id || '',
    monthly_amount: retainer?.monthly_amount ?? '',
    // Existing retainers show exactly what is stored — including nothing. An end date
    // is never guessed from the old duration here; the migration already backfilled
    // the ones it could, and inventing the rest would be indistinguishable from a real
    // contract date.
    start_date: retainer?.start_date ?? (retainer ? '' : todayISO()),
    end_date: retainer?.end_date ?? '',
  });
  const [error, setError] = useState(null);
  const reloadAlerts = useReloadAlerts();
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const [clients, partners] = data || [[], []];
  const fixed = form.billing_mode === 'fixed';
  const t = fixed ? he.projects : he.retainers;
  const asAmount = (v) => (v !== '' && v != null ? Number(v) : null);

  // `Select` renders no placeholder option, so a `''` state value displays the FIRST
  // entry while the state stays empty. Derive the effective ids so what is shown is
  // what gets sent — see the same note in JobModal, where the mismatch silently
  // emptied the retainer picker.
  const clientId = form.client_id || (clients[0] ? String(clients[0].id) : '');
  // The issuer needs no such fallback: its first option IS the empty value, "טרם הוחלט"
  // (074), so what is shown is what is sent.

  const submit = async () => {
    try {
      const body = {
        title: form.title,
        client_id: Number(clientId) || null,
        revenue_stream: form.revenue_stream || lookups.streamOptions[0]?.value,
        issuing_partner_id: Number(form.issuing_partner_id) || null,
        billing_mode: form.billing_mode,
        // Only the amount that belongs to the type is sent; the server writes nothing else.
        monthly_amount: fixed ? null : asAmount(form.monthly_amount),
        fixed_amount: fixed ? asAmount(form.fixed_amount) : null,
        // Explicit nulls, not omissions: the server preserves a stored date for any
        // key the body leaves out, so clearing a term has to be said out loud.
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        // No `status`: see the note by the fields. A new retainer is created active by
        // the server's default, and an edit leaves an existing one exactly as it was.
      };
      if (isNew) await retainersApi.create(body);
      else await retainersApi.update(retainer.id, body);
      // A new retainer, or a changed term, changes which months are waiting to be
      // invoiced — the money dot's amber — so the sidebar re-reads now.
      reloadAlerts();
      onSaved();
    } catch (e) {
      setError(e);
    }
  };

  return (
    <Modal
      title={fixed
        ? (isNew ? t.newProject : t.editProject)
        : (isNew ? t.newRetainer : t.editRetainer)}
      onClose={onClose}
      onSave={submit}
      saveDisabled={!form.title || !clientId}
    >
      <TextInput label={fixed ? t.projectTitle : t.retainerTitle} value={form.title} onChange={set('title')} />
      {!isNew && (
        <Select
          label={he.projects.billingMode}
          value={form.billing_mode}
          onChange={set('billing_mode')}
          options={[
            { value: 'monthly', label: he.projects.modeMonthly },
            { value: 'fixed', label: he.projects.modeFixed },
          ]}
          disabled={modeLocked}
        />
      )}
      <ClientPicker
        label={he.jobs.client}
        value={clientId}
        onChange={(id) => setForm((f) => ({ ...f, client_id: id }))}
        clients={clients}
        disabled={!isNew}
      />
      <Select label={he.jobs.stream} value={form.revenue_stream} onChange={set('revenue_stream')} options={lookups.streamOptions} />
      <Select
        label={he.jobs.defaultIssuer}
        value={form.issuing_partner_id}
        onChange={set('issuing_partner_id')}
        options={issuerOptions(partners)}
      />
      {fixed
        ? <NumberInput label={t.price} value={form.fixed_amount} onChange={set('fixed_amount')} />
        : <NumberInput label={t.monthlyAmount} value={form.monthly_amount} onChange={set('monthly_amount')} />}
      <DateInput label={t.startDate} value={form.start_date} onChange={set('start_date')} />
      <DateInput label={t.endDate} value={form.end_date} onChange={set('end_date')} />
      {/* The status is NOT edited here. Ending an engagement is its own act with its own
          dialog (RetainerStatusModal, opened from the retainers list and the retainer
          screen), which writes that one column and nothing else. A dropdown here would be
          a second way to do the same thing, silently, in the middle of a form about titles
          and dates. This form leaves the key out of its body entirely, and the server
          keeps whatever is stored. */}
      {error && <ErrorText error={error} />}
    </Modal>
  );
}
